import {
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import {
  ColumnComponent,
  ColumnDefinition,
  Filter,
  Sorter,
  TabulatorFull as Tabulator
} from 'tabulator-tables';
import { uniqueId, uniq } from 'lodash';
import { IMenu } from './types';
import {
  BenchmarkService,
  TimeoutWrapperService,
  TimeoutWrapper,
  DialogService
} from '@nrwl/common-lib';
import { ActivatedRoute } from '@angular/router';
import { NavigationProvider } from '@nrwl/navigation-abstract';
import { Subject, Unsubscribable } from 'rxjs';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'viavi-table',
  templateUrl: './viavi-table.component.html',
  styleUrls: ['./viavi-table.component.scss'],
  providers: [TimeoutWrapper]
})
// eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
export class ViaviTableComponent implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
  @Input() selector: string;
  // eslint-disable-next-line
  @Input() tableConfigurations: any;
  @Input() isActionEnabled: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() template: TemplateRef<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() resetCheckbox: Subject<string[]>;
  @Input() cartMode?: boolean = false;
  @Input() cartPage?: boolean = false;
  @Input() options;
  @Input() showToolbar?: boolean = true;
  //eslint-disable-next-line
  @Output() rowClicked?: EventEmitter<any> = new EventEmitter();
  //eslint-disable-next-line
  @Output() sendRowAction?: EventEmitter<any> = new EventEmitter();
  //eslint-disable-next-line
  @Output() sendPopupAction?: EventEmitter<any> = new EventEmitter();
  //eslint-disable-next-line
  @Output() onDataFiltered?: EventEmitter<boolean> = new EventEmitter();
  @Output() sendSeletedItem?: EventEmitter<{ type: string }> = new EventEmitter();
  //eslint-disable-next-line
  @Output() removeData?: EventEmitter<any> = new EventEmitter();
  //eslint-disable-next-line
  @Output() removeAllRows?: EventEmitter<any> = new EventEmitter();
  resetCheckboxUnsubscribe: Unsubscribable;
  table: Tabulator;
  isHideClearGroup = false;
  //eslint-disable-next-line
  myGroupBy: any = [];
  isRowSelected = false;
  selection = [];
  tableObj;
  id: string;
  clearFilterLabel = 'Clear Filter';
  isShowClearFilter = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedTableConfig: any;
  isLoading = true;
  selectedRows = [];
  selectedCount = 0;
  checkboxCustomClass = 'custom-checkbox-cell';
  treeCheckboxCustomClass = 'custom-checkbox-cell tree-custom-checkbox-cell';
  isFilterApplied: string;
  isRefresh = false;
  preDefinedHeaderFilters: string;
  selectMultipleList: {
    fieldName: string;
    value: string[];
  }[] = [];
  isWarningMessageAvailable = false;
  timeout: TimeoutWrapper;
  overallChildProcess = false;
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  constructor(
    private elementRef: ElementRef,
    private readonly benchmarkService: BenchmarkService,
    private activateRoute: ActivatedRoute,
    private readonly navigator: NavigationProvider,
    private readonly timeoutWrapperService: TimeoutWrapperService,
    timeout: TimeoutWrapper,
    private dialogService: DialogService
  ) {
    this.timeout = timeout;
    this.id = uniqueId('viavi-table-');
  }
  ngAfterViewChecked(): void {
    if (this.table && this.myGroupBy.length > 0) {
      this.isHideClearGroup = true;
    } else {
      this.isHideClearGroup = false;
    }
  }

  // eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.options = changes.options.currentValue;
    }
    if (this.table && changes.tableConfigurations) {
      this.isWarningMessageAvailable = false;
      const oldRowSelection = Object.assign([], this.table.getSelectedData());
      const filter = [...this.table.getHeaderFilters()];
      if (this.myGroupBy.length > 0) {
        this.table.clearFilter(true);
      }
      this.benchmarkService.registerPageEvent('TabulatorReplaceRendering');
      this.isWarningMessageAvailable = this.checkWarningMessage();
      this.table
        .replaceData(this.tableConfigurations.data)
        .then(() => {
          this.benchmarkService.logPageEventTime(
            'Tabulator replace rendering',
            'TabulatorReplaceRendering'
          );

          // eslint-disable-next-line max-len
          this.setMultiselectOptions(filter); // This should call first since tabulator update column definition not working as expected. Inside that methid we are update the entire columns
          this.table.deselectRow();
          if (oldRowSelection.length > 0) {
            oldRowSelection.forEach(element => {
              if (!element.isCheckboxDisabled) {
                this.table.selectRow(element.id);
              }
            });
          }
          if (oldRowSelection.length === 0) {
            this.isRefresh = false;
          }
          if (this.myGroupBy.length > 0) {
            this.table.setGroupBy(this.myGroupBy);
          }
          if (this.myGroupBy.length > 0 && filter.length > 0) {
            filter.forEach(element => {
              this.table.setHeaderFilterValue(element.field, element.value);
            });
          }
          this.selectActiveRows();
          if (this.isRefresh) this.showSelectedList();
        })
        .catch(err => console.warn(err));
    }
  }

  ngOnInit(): void {
    this.isWarningMessageAvailable = this.checkWarningMessage();
    this.benchmarkService.registerPageEvent('TabulatorRendering');
    const snapshot = this.activateRoute.snapshot;
    this.isFilterApplied = snapshot.queryParams ? snapshot.queryParams['isFilterApplied'] : null;
    this.tableObj = Object.assign({}, this.tableConfigurations);
    this.dynamicDivCreation();
    this.isHideClearGroup = this.tableConfigurations.groupBy;
    this.tableObj.debugInvalidOptions = false;
    this.tableObj.columns = this.getTableColumns();
    this.table = new Tabulator('#' + this.id, this.tableObj);
    if (this.tableConfigurations && this.table) {
      this.tableConfigurations.columns.forEach(
        (
          //eslint-disable-next-line
          element: { extras: { isGroupBy: any }; tabulatorInfo: { field: any } },
          index: string | number
        ) => {
          if (element.extras?.isGroupBy) {
            this.tableConfigurations.columns[index].tabulatorInfo.headerMenu = this.headerMenu;
          }
        }
      );
    }
    //#region  Tabulator Event Handler
    this.table.on('tableBuilt', () => {
      this.isLoading = false;
      if (!this.cartMode) {
        this.toggleFilter();
        if (this.table.getData().some(row => row.isChecked)) {
          this.preloadDefaultSelection();
        }
      }
      if (this.table.getGroups()?.length > 0)
        this.myGroupBy = this.table.getGroups()[0].getTable().options.groupBy;
      this.isHideClearGroup = true;
      if (!this.cartMode) {
        this.retainSort();
      }
      this.table.on('rowSelectionChanged', (data, rows) => {
        this.selection = this.table.getSelectedData();
        this.selection = this.removeParentRows(this.selection);

        if (this.tableConfigurations.dataTree && !this.overallChildProcess) {
          this.overallChildProcess = true;
          this.checkOverallChildRows(rows);
        }
        this.selectedCount = this.selection.length;
      });

      this.table.on('dataFiltering', () => {
        const setEmptyFilter = this.table
          .getHeaderFilters()
          .filter(filter => Array.isArray(filter.value))
          .filter(deepFilter => deepFilter.value.length == 0);
        setEmptyFilter?.forEach(element => {
          this.table.setHeaderFilterValue(element.field, null);
        });
        this.isShowClearFilter = this.table.getHeaderFilters().length === 0 ? false : true;
        if (!this.isShowClearFilter) this.table.redraw();
      });
      // eslint-disable-next-line max-len
      this.table.on('dataFiltered', (filters: Filter[]) => {
        this.setFilterinStorage(filters);
        if (filters?.length > 0) {
          this.addFilterParamsToUrl();
        }
        this.timeout.add(() => {
          this.onDataFiltered.next(true);
        }, 500);
      });

      this.benchmarkService.logPageEventTime('Tabulator rendering', 'TabulatorRendering');
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.table.on('rowClick', (event: any, row) => {
      if (
        event.target.innerHTML === 'more_horiz' ||
        event.target.className.includes(this.checkboxCustomClass) ||
        event.target.className.includes('mat-menu-trigger')
      )
        return;
      if (!event.defaultPrevented) {
        this.rowClicked.next(row.getData());
      }
      if (this.tableConfigurations.dataTree) {
        row.treeToggle();
      }
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.table.on('cellClick', (event: any, row: any) => {
      const rowData = row.getData();
      if (row.getValue()?.emit) {
        this.sendRowAction.next({
          label: 'singleSelect',
          cellValue: row.getValue(),
          rowData: rowData
        });
      }
      if (event?.target?.id === 'logs' && event?.target?.name?.length > 0) {
        const temp = {
          action: 'logs',
          rowData: row.getData(),
          url: event?.target?.name
        };
        this.sendRowAction.next(temp);
      }
      if (event.target.innerHTML === 'Select' || event.target.innerHTML === 'Specs') {
        const temp = {
          id: row.getData().id,
          action: 'select',
          rowData: row.getData()
        };
        this.sendRowAction.next(temp);
      }
      if (!rowData.removed) {
        rowData.removed = true;
        this.removeData.emit(rowData);
      } else {
        rowData.removed = false;
        this.removeData.emit(rowData);
      }
      const currRow = row.getRow();
      if (currRow.isSelected() && !currRow.getData()?.isCheckboxDisabled) {
        currRow.deselect();
      }
      this.selectedTableConfig = row.getData().actions;
      if (event.target.innerHTML.indexOf('more_horiz') !== -1) {
        const defintion = this.table.getColumn('actions').getDefinition();
        defintion.clickMenu = this.getMenu();
        this.table.updateColumnDefinition('actions', defintion);
      }
    });
    this.table.on('menuOpened', () => {
      const elements = document.querySelectorAll('.tabulator-popup-container');
      if (sessionStorage.getItem('theme') === 'dark') elements[0].classList.add('dark-theme');

      const popupElement = elements[0] as HTMLDivElement;
      popupElement.style.top = popupElement.style.top.includes('-')
        ? '0.3rem'
        : popupElement.style.top;
    });
    this.table.on('popupOpened', component => {
      const elements = document.querySelectorAll('.testlist_view');
      const elemts = document.querySelectorAll('.tabulator-popup-container');
      if (elemts && elemts.length > 0 && elemts[0].innerHTML === 'undefined') {
        elemts[0].remove();
      }
      if (sessionStorage.getItem('theme') === 'dark') elemts[0].classList.add('dark-theme');
      elements.forEach(element => {
        element.addEventListener('click', this.viewClick.bind(this, component));
      });
    });

    this.table.on('dataGrouped', () => {
      const currentPageSize = this.table.getPageSize();
      if (this.myGroupBy.length > 0 && currentPageSize !== 50) {
        this.isHideClearGroup = true;
        this.table.setPageSize(50);
      } else if (this.myGroupBy.length === 0) {
        this.isHideClearGroup = false;
        this.table.setPageSize(10);
      }
    });

    this.table.on('renderComplete', () => {
      const activeRows = this.returnActiveSelectedAfterFiltered();
      this.selection = [...activeRows];
      this.selectedCount = this.selection.length;

      const temp = document.querySelector('.tabulator-placeholder') as HTMLDivElement;
      const scrollbar = document.querySelector('.tabulator-tableholder') as HTMLDivElement;
      if (this.table.getRows('visible').length == 0) {
        if (temp) temp.style.display = 'flex';
        if (scrollbar) scrollbar.style.overflow = 'hidden';
      } else {
        if (temp) temp.style.display = 'none';
        if (scrollbar) scrollbar.style.overflow = 'auto';

        // clearing filtered field for nested children when filtered cleared
        const filters = this.table.getFilters(true);
        if (filters.length === 0 && this.tableConfigurations.dataTree) {
          for (const rowData of this.table.getRows()) {
            if (rowData.getData()._children) {
              for (const childData of rowData.getData()._children) {
                childData.filtered = false;
              }
            }
          }
        }
      }
    });
    this.table.on('selectableCheck', (row: any) => {
      return !row.isCheckboxDisabled;
    });
    this.resetCheckboxUnsubscribe = this.resetCheckbox?.subscribe(data => {
      if (data.length) {
        data.forEach(selectedId => {
          this.table.deselectRow(selectedId);
        });
      }
    });

    //#endregion
  }

  checkWarningMessage(): boolean {
    return this.tableConfigurations.data?.some(row => row.isWarningMessage);
  }
  filteredCount(): number {
    return this.table.getDataCount('active');
  }
  filteredItems(): any {
    return this.table.getData('active');
  }
  returnActiveSelectedAfterFiltered(): any {
    return this.table
      .getRows('active')
      .filter(function (obj) {
        return obj.isSelected() == true;
      })
      .map(function (obj) {
        return obj.getData();
      });
  }
  addFilterParamsToUrl(): void {
    const path = this.activateRoute.routeConfig?.path;
    if (path && Object.keys(this.activateRoute.snapshot.queryParams).length !== 0) {
      this.navigator.navigateToDashboard([path], {
        // Navigating without adding to the history to retain exact url.
        replaceUrl: true,
        queryParams: {
          isFilterApplied: 'true'
        }
      });
    }
  }

  preloadDefaultSelection(): void {
    this.table.getRows().forEach(element => {
      if (element.getData().isChecked) this.table.selectRow(element.getData().id);
    });
    this.showSelectedList();
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  setFilterinStorage(filters: any[]) {
    const emptyFilter = filters.filter(filter => Array.isArray(filter.value));
    if (emptyFilter.length > 0) {
      // TODO need to add with unique values , now handled this problem via columnName but we don't have control to have unique caolumn name
      filters = filters.filter(
        actualFilter =>
          !emptyFilter.filter(
            compareArray =>
              compareArray.field === actualFilter.field && actualFilter.value.length === 0
          ).length
      );
    }
    const filterUniq = [...new Map(filters.map(v => [v.field, v])).values()];
    localStorage.setItem(
      'tabulator-' + this.table.options.persistenceID + '-filter',
      filterUniq && filterUniq.length > 0 ? JSON.stringify(filterUniq) : null
    );
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  setHeaderFilters(): void {
    this.preDefinedHeaderFilters = localStorage.getItem(
      'tabulator-' + this.table.options.persistenceID + '-filter'
    );
    if (this.preDefinedHeaderFilters) {
      const cols = JSON.parse(this.preDefinedHeaderFilters);
      cols?.forEach(element => {
        if (this.table.getColumn(element.field).getDefinition().headerFilter !== 'list')
          this.table.setHeaderFilterValue(element.field, element.value);
        else {
          /**<TODO> Need to remove this conditions and work on upgrade version later.
          This is not needed for newer version< target in release 4.0> */
          this.table.setHeaderFilterValue(element.field, element.value);
          this.timeoutWrapperService.add(() => {
            this.table.getHeaderFilters().forEach(x => {
              this.table.setHeaderFilterFocus(x.field);
            });
            this.timeoutWrapperService.add(() => {
              document.getElementById('buttontxt').click();
              const focusField = this.table
                .getColumns()
                .find(
                  x =>
                    x.isVisible() &&
                    x.getDefinition().headerFilter &&
                    x.getDefinition().headerFilter !== 'list' &&
                    x.getField() !== 'rowSelection'
                )
                .getField();
              this.table.setHeaderFilterFocus(focusField);
            }, 200);
          }, 0);
        }
      });
    }
  }
  //eslint-disable-next-line
  getTableColumns(): any {
    if (this.isActionEnabled && this.tableConfigurations?.columns) {
      this.tableConfigurations?.columns.unshift({
        tabulatorInfo: {
          formatter: this.checkboxFormatter,
          titleFormatter: this.checkboxFormatter,
          field: 'rowSelection',
          hozAlign: 'center',
          headerSort: false,
          width: this.tableConfigurations.dataTree ? 68 : 44,
          resizable: false,
          visible: true,
          cssClass: this.tableConfigurations.dataTree
            ? this.treeCheckboxCustomClass
            : this.checkboxCustomClass
        }
      });
    }
    return this.tableConfigurations.columns.map(
      //eslint-disable-next-line
      (col: { tabulatorInfo: any }) => col.tabulatorInfo
    );
  }

  //eslint-disable-next-line
  selectInvidualChildRows(cell: any): void {
    if (this.table.options.dataTree) {
      const row = cell.getRow();
      const parent = row?.getTreeParent();
      if (parent) {
        const children = parent?.getTreeChildren();
        const selectedChild = row.getData();
        if (children && children.length > 0) {
          children.forEach(ch => {
            const chData = ch.getData();
            if (chData.uniqueKey !== selectedChild.uniqueKey) {
              parent.select();
              ch.deselect();
            }
          });
          if (children.length === children.filter(item => !item.isSelected()).length) {
            parent.deselect();
          }
        }
      }
      if (!row.isSelected()) {
        const children = row?.getTreeChildren();
        if (children && children.length > 0) {
          children.forEach((el, index) => {
            if (index === 0) {
              el.deselect();
            } else {
              el.deselect();
            }
          });
        }
      }
    }
  }

  //eslint-disable-next-line
  checkboxFormatter = (cell: any, formatterParams: any, onRendered: any): any => {
    const container = document.createElement('div');
    let isIconAdded = false;
    let warningMessage = null;
    let checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.addEventListener('click', e => {
      this.timeout.add(() => {
        if (!cell.getValue()) {
          this.selectInvidualChildRows(cell);
        }
      }, 1);
      e.stopPropagation();
    });
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const _this = this;
    let blocked = false;
    if (typeof cell.getRow === 'function') {
      const row = cell.getRow();
      const data = cell.getRow().getData();
      checkbox.disabled = data.isCheckboxDisabled;
      isIconAdded = data.isWarningMessage;
      warningMessage = data.warningMessage;
      if (row._getSelf().type === 'row') {
        //eslint-disable-next-line
        checkbox.addEventListener('change', e => {
          if (_this.table.options.selectableRangeMode === 'click') {
            if (!blocked) {
              row.toggleSelect();
              this.selectActiveRows();
              this.sendSeletedItem.next({
                type: 'single'
              });
              if (this.table && this.table.getSelectedData().length === 0) {
                this.refreshTable();
              }
            } else {
              this.sendSeletedItem.next({
                type: 'single'
              });
              blocked = false;
            }
          } else {
            row.toggleSelect();
            this.selectActiveRows();
            this.sendSeletedItem.next({
              type: 'single'
            });
            if (this.table && this.table.getSelectedData().length === 0) {
              this.refreshTable();
            }
          }
        });
        if (this.table.options.selectableRangeMode === 'click') {
          checkbox.addEventListener('click', function (e) {
            blocked = true;

            _this.table.modules.selectRow.handleComplexRowClick(row._row, e);
          });
        }

        checkbox.checked = this.tableConfigurations.dataTree
          ? (row.isSelected && row.isSelected()) ||
            (this.checkSelectedRows(data.id) && !row?.getTreeParent())
          : (row.isSelected && row.isSelected()) || this.checkSelectedRows(data.id);
        if (checkbox.checked) {
          row.select();
        }
        this.table.modules.selectRow.registerRowSelectCheckbox(row, checkbox);
      } else {
        checkbox = null;
      }
    } else {
      if (this.tableConfigurations.hideSelectAll) {
        checkbox.style.display = 'none';
      }
      //eslint-disable-next-line
      checkbox.addEventListener('change', e => {
        if (this.table.modules.selectRow.selectedRows.length) {
          this.table.deselectRow([]);
          this.sendSeletedItem.next({
            type: 'multiple'
          });
          this.refreshTable();
        } else {
          if (
            typeof this?.options?.cartLength !== 'undefined' &&
            typeof this?.options?.selectionLimit !== 'undefined' &&
            this.options.cartLength + this.filteredCount() > this.options.selectionLimit
          ) {
            this.showBulkTestLimitDialog();
            return;
          } else {
            const filteredRows = this.table
              .getRows('active')
              .filter(data => !data.getData().isCheckboxDisabled);
            this.table.selectRow(filteredRows);
            this.sendSeletedItem.next({
              type: 'multiple'
            });
          }
        }
      });
      if (!this.table.getData().some(rows => rows.isCheckboxDisabled)) {
        if (this.table.getSelectedData().length === this.table.getData().length)
          checkbox.checked = true;
        else if (this.table.getSelectedData().length !== 0) checkbox.indeterminate = true;
        else checkbox.checked = false;
      } else {
        const disabledCounts = this.table.getData().filter(data => data.isCheckboxDisabled).length;
        if (this.table.getSelectedData().length === disabledCounts) checkbox.disabled = true;
        else if (this.table.getSelectedData().length !== 0) checkbox.indeterminate = true;
        else checkbox.checked = false;
      }
      this.table.modules.selectRow.registerHeaderSelectCheckbox(checkbox);
    }
    if (isIconAdded) {
      const temp = document.createElement('i');
      temp.className = 'mdi mdi-progress-clock';
      temp.style.fontSize = '2rem';
      temp.style.color = '#f57902';
      temp.title = warningMessage;
      container.appendChild(temp);
      checkbox.style.marginRight = '3px';
      container.appendChild(checkbox);
      return container;
    } else {
      if (this.isWarningMessageAvailable) {
        checkbox.style.marginLeft = '1rem';
      }
      return checkbox;
    }
  };

  //eslint-disable-next-line
  checkSelectedRows(id: any): boolean {
    return this.selectedRows.some(rows => rows.id === id);
  }

  // eslint-disable-next-line
  viewClick(component: any, event): void {
    const cellValue = component._cell.value;
    const classList = event.target['classList'].value.split(' ');
    const filteredData = cellValue.filter(item => classList.find(name => item.type === name));
    this.sendPopupAction.emit(filteredData);
    const element = document.getElementById('test-assigned');
    element.parentElement.style.display = 'none';
  }
  dynamicDivCreation(): void {
    const div = document.createElement('div');
    div.id = this.id;
    div.className = 'viavi-tabulator';
    this.elementRef.nativeElement.querySelector('#filter-controls').after(div.cloneNode(true));
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  toggleFilter(event?: any): void {
    const preDefinedHeaderFilters = JSON.parse(this.getFilterFromLocalStorage());
    if (this.table && this.table.getHeaderFilters().length === 0 && !this.isFilterApplied) {
      this.setHeaderFilterColumn();
      this.retainSort();
    } else {
      if (
        !event &&
        this.isFilterApplied &&
        this.isFilterApplied === 'true' &&
        preDefinedHeaderFilters
      ) {
        this.isShowClearFilter = true;
        this.setHeaderFilterColumn();
        this.setHeaderFilters();
      } else {
        if (
          this.isFilterApplied === 'false' ||
          (this.isFilterApplied === 'true' && event) ||
          (this.isFilterApplied === 'true' && !preDefinedHeaderFilters)
        ) {
          this.isFilterApplied = null;
          this.toggleFilter();
        } else {
          this.isShowClearFilter = true;
          this.clearFilter();
        }
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  setMultiselectOptions(predefinedFilter): void {
    this.table.getColumns().filter(col => {
      if (col.getDefinition().headerFilter == 'list') {
        this.table.getColumn(col.getField()).getDefinition().headerFilterParams['values'] =
          this.loadUniqueValues(this.table.getColumn(col.getField()).getDefinition());
      }
    });
    this.table.setColumns(this.table.getColumnDefinitions());
    predefinedFilter.forEach(element => {
      this.table.setHeaderFilterValue(element.field, element.value);
    });
  }
  clearFilter(): void {
    this.table.getColumns().forEach(col => {
      this.table.setHeaderFilterValue(col, '');
    });
    this.selectMultipleList = [];
    this.table.clearFilter(false);
  }

  setGroup(): void {
    this.myGroupBy = [];
    this.isHideClearGroup = false;
    this.table.setGroupBy(this.myGroupBy);
    this.table.setPageSize(10);
  }
  // eslint-disable-next-line
  removeGroup(event: any): void {
    const index = this.myGroupBy.indexOf(event);
    if (index > -1) {
      // only splice array when item is found
      this.myGroupBy.splice(index, 1);
    }
    this.table.setGroupBy(this.myGroupBy);
    if (this.myGroupBy.length === 0) {
      this.isHideClearGroup = false;
    }
  }
  getColumnName(fieldName: string): string {
    const result = this.table.getColumns().find(x => x.getField() === fieldName);
    return result.getDefinition().title;
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getMenu() {
    const menuArray = [];
    this.selectedTableConfig?.forEach(menuItem => {
      menuItem?.actions?.forEach(menu => {
        menuArray.push({
          label:
            '<div class="action-block"><i class="action-icon mdi ' +
            menu.iconName +
            ' ' +
            '"></i><span class="action-label"> ' +
            menu.label +
            '</span></div>',
          action: (e, cell) => {
            const currRow = cell.getRow();
            if (currRow.isSelected()) {
              currRow.deselect();
            }
            const temp = {
              label: menu.option,
              id: cell.getData().id,
              rowData: cell.getData()
            };
            this.sendRowAction.next(temp);
            e.preventDefault();
          },
          disabled: menu.disable,
          defaultAction: menu.isDefaultAction
        });
      });
    });
    const defaultAction = menuArray.find(x => x.defaultAction);
    if (defaultAction) {
      menuArray.unshift(Object.assign({}, defaultAction));
      menuArray[0].label = menuArray[0]?.label + '<hr>';
    }
    return menuArray;
  }
  // eslint-disable-next-line
  dataFilterFunc(headerValue: any, rowValue: any, _rowdata: any, _filterparams: any) {
    if (headerValue.length > 0) return headerValue.find(x => x === rowValue);
    else return true;
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loadUniqueValues(element: ColumnDefinition): any[] {
    const colData = this.table
      .getData()
      .map(data => data[element.field.split('.')[0]][element.field.split('.')[1]]);
    const list1 = colData.filter(
      (value, index, resultArray) => resultArray.indexOf(value) === index
    );

    const obj = [];
    list1.forEach(x => {
      obj.push({ value: x, label: x });
    });
    return obj;
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  headerMenu = (element, component: ColumnComponent) => {
    const menu = [];
    const headerMenuActions: IMenu[] = [];
    const myGroup = this.myGroupBy;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const table = this.table;
    this.tableConfigurations.columns.forEach(element => {
      if (element.tabulatorInfo.field === component.getField()) {
        if (element.extras?.isGroupBy) {
          headerMenuActions.push({
            title: 'GroupBy',
            key: 'GroupBy',
            columnName: element.tabulatorInfo.field,
            isSelected: myGroup.includes(element.tabulatorInfo.field),
            iconOn: 'mdi-checkbox-marked',
            iconOff: 'mdi-checkbox-blank-outline',
            isVisible: true
          });
        }
      }
    });
    for (const action of headerMenuActions) {
      //create checkbox element using font awesome icons
      const icon = document.createElement('i');
      icon.classList.add('mdi');
      icon.style.fontSize = '2rem';
      icon.classList.add(action.isSelected ? action.iconOn : action.iconOff);

      //build label
      const label = document.createElement('span');
      const title = document.createElement('span');

      title.textContent = ' ' + action.title;

      label.appendChild(icon);
      label.appendChild(title);
      //create menu item
      menu.push({
        label: label,
        //eslint-disable-next-line
        action: function (e) {
          //prevent menu closing
          // e.stopPropagation(); < TODO> Requirement needs to check here
          if (action) {
            switch (action.key) {
              case 'GroupBy': {
                if (!myGroup.includes(action.columnName)) {
                  myGroup.push(action.columnName);
                  action.isSelected = true;
                  icon.classList.remove(action.iconOff);
                  icon.classList.add(action.iconOn);
                } else {
                  myGroup.splice(myGroup.indexOf(action.columnName), 1);
                  action.isSelected = false;
                  icon.classList.add(action.iconOff);
                  icon.classList.remove(action.iconOn);
                }
                table.setGroupBy(myGroup);
                break;
              }
              case component.getField(): {
                icon.classList.remove(action.iconOff);
                icon.classList.add(action.iconOn);
                break;
              }
            }
          }
        }
      });
    }
    return menu;
  };
  retainSort(): void {
    if (this.table.getSorters().length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const predefinedSort: Sorter[] = [];
      this.table.getSorters().forEach(sortElement => {
        predefinedSort.push({
          column: sortElement.field,
          dir: sortElement.dir
        });
      });
      this.table.setSort(predefinedSort);
    }
  }

  setHeaderFilterColumn(): void {
    this.table.getColumnDefinitions().forEach((element, index) => {
      const tabulatorInfo = this.tableConfigurations.columns.filter(
        col => col.tabulatorInfo.field === element.field
      );
      if (
        element.field &&
        // eslint-disable-next-line no-prototype-builtins
        element.hasOwnProperty('headerFilter')
      ) {
        if (
          tabulatorInfo.find(conf => conf.extras) &&
          tabulatorInfo.find(conf => conf.extras).extras.isMultiselectFilter
        ) {
          element.headerFilter = 'list';
          element.headerFilterParams = {
            multiselect: true,
            values: this.loadUniqueValues(element),
            //<TODO Need to write logic to filter onClick
            itemFormatter: (label, value, item, ele) => {
              const tabulatorCol = document.querySelectorAll('.tabulator-col');
              let searchInput;
              tabulatorCol.forEach(htmlElement => {
                if (htmlElement.attributes['tabulator-field'].value == element.field) {
                  searchInput = htmlElement.querySelectorAll('.tabulator-header-filter')[0]
                    .children;
                  searchInput[0].value = uniq(searchInput[0].value.split(',')).toString();
                }
              });
              if (sessionStorage.getItem('theme') === 'dark') ele.className = 'dark-theme';
              return label;
            }
          };
          element.headerFilterFunc = this.dataFilterFunc;
        }
        this.table.getColumnDefinitions()[index].headerFilter = element.headerFilter;
        this.table.getColumnDefinitions()[index].visible = element.visible;
      }
    });
    this.table.setColumns(this.table.getColumnDefinitions());
  }
  showSelectedList(): void {
    const oldRowSelection = Object.assign([], this.table.getSelectedData());
    this.table.replaceData(this.table.getSelectedData()).then(() => {
      this.table.deselectRow();
      oldRowSelection.forEach(element => {
        this.table.selectRow(element.id);
      });
      this.selectActiveRows();
      this.isRefresh = true;
    });
  }
  refreshTable(): void {
    const oldRowSelection = Object.assign([], this.table.getSelectedData());
    this.table.replaceData(this.tableConfigurations.data).then(() => {
      oldRowSelection.forEach(element => {
        this.table.selectRow(element.id);
      });
      this.table.deselectRow();
      this.selectActiveRows();
      this.isRefresh = false;
    });
  }

  showAllList(): void {
    const oldRowSelection = Object.assign([], this.table.getSelectedData());
    this.table.replaceData(this.tableConfigurations.data).then(() => {
      this.table.deselectRow();
      oldRowSelection.forEach(element => {
        this.table.selectRow(element.id);
      });
      this.selectActiveRows();
      this.isRefresh = false;
    });
  }

  getFilterFromLocalStorage(): string | null {
    return localStorage.getItem('tabulator-' + this.table.options.persistenceID + '-filter');
  }
  selectActiveRows(): void {
    this.selectedRows = this.table.getSelectedData();
    this.selection = this.returnActiveSelectedAfterFiltered();
    this.selectedCount = this.selection.length;
  }

  //eslint-disable-next-line
  removeParentRows(selection: any): any {
    return selection.filter(item => item.rowType !== 'parent');
  }
  removeAllCartData(value: boolean): void {
    this.removeAllRows.emit(value);
  }
  disableRemoveButton(): boolean {
    return this.tableConfigurations.data.filter(item => !item.removed).length === 0;
  }
  disableUndoButton(): boolean {
    return this.tableConfigurations.data.filter(item => item.removed).length === 0;
  }
  //eslint-disable-next-line
  checkOverallChildRows(rows: any): void {
    const selectionRows = [];
    const deSelectRows = [];
    for (const element of rows) {
      if (element.isSelected()) {
        const children = element.getTreeChildren();
        if (children?.length) {
          const filteredChildren = children.filter(childItem => childItem.getData().filtered);
          if (filteredChildren?.length) {
            for (const [index, item] of filteredChildren.entries()) {
              if (index === 0) {
                selectionRows.push(item);
              } else if (item.isSelected()) {
                deSelectRows.push(item);
              }
            }
          } else {
            const filteredItems = children.filter(item => item.isSelected());
            if (filteredItems.length === 0 || filteredItems.length === children.length) {
              for (const [index, element] of children.entries()) {
                if (index === 0) {
                  selectionRows.push(element);
                } else if (element.isSelected()) {
                  deSelectRows.push(element);
                }
              }
            }
          }
          const checkboxDisabledItems = children.filter(item => item.getData()?.isCheckboxDisabled);
          if (checkboxDisabledItems?.length) {
            checkboxDisabledItems.forEach(item => deSelectRows.push(item));
          }
        }
      }
    }
    this.table.selectRow(selectionRows);
    this.table.deselectRow(deSelectRows);
    this.overallChildProcess = false;
  }
  showBulkTestLimitDialog(): void {
    const params = { action: 'bulk.selection' };
    const param = {
      id: params.action,
      alternateMsg: `You can only select ${this.options.selectionLimit} endpoints. Please reduce your amount and try again.`,
      alternateTitle: 'Endpoint limit exceeded',
      callBackService: this
    };
    this.dialogService.showDialog('confirm', 'body', param);
  }
  ngOnDestroy(): void {
    if (this.resetCheckboxUnsubscribe) {
      this.resetCheckboxUnsubscribe.unsubscribe();
    }
  }
}
